import React, { useState, useEffect, useRef } from 'react';
import { closedLotWord, ClosedLotStatus, SETTLING_GRACE_MS } from '../../utils/saleState';
import {
  toDate,
  formatTimeRemaining,
  isInSoftCloseWindow,
  DEFAULT_SOFT_CLOSE_WINDOW_SECONDS,
} from '../../utils/lotTiming';
import { serverNow } from '../../utils/serverClock';

interface CountdownTimerProps {
  endTime: Date;
  scheduledEndTime?: Date; // Show if extended
  /** The sale's biddingHardStop (Timestamp or Date). The clock runs to the EARLIER of the two. */
  hardStop?: unknown;
  softCloseWindowSeconds?: number;
  onTimeExpired?: () => void;
  size?: 'small' | 'medium' | 'large';
  /** closedLotStatus(auction, lot). Display only. */
  closedStatus?: ClosedLotStatus;
}

/**
 * DISPLAY ONLY — this clock never blocks a bid (Oct 2026: the browser is never the authority).
 *
 * NO STICKY "EXPIRED" STATE. The old version set an isExpired flag at 0:00 and never cleared it, so
 * when a soft-close extension later moved endTime forward the page kept showing CLOSED on a lot
 * still taking bids — even with live data. Now "expired" is evaluated fresh on every tick from
 * `now >= end`, so a later end time arriving un-closes the display. For the first few seconds past
 * zero it says "Closing…" (SETTLING_GRACE_MS, the same grace the tiles use): an extension written
 * by a last-second bid may still be on its way to this browser.
 */
export const CountdownTimer: React.FC<CountdownTimerProps> = ({
  endTime,
  scheduledEndTime,
  hardStop,
  // Was hardcoded 900 while the server extended inside 180 — the countdown
  // showed a 15-minute "closing soon" the engine would not honour.
  softCloseWindowSeconds = DEFAULT_SOFT_CLOSE_WINDOW_SECONDS,
  onTimeExpired,
  size = 'medium',
  closedStatus = 'closed',
}) => {
  const stop = toDate(hardStop);
  const end = stop && stop.getTime() < endTime.getTime() ? stop : endTime;
  const endMs = end.getTime();

  const [nowMs, setNowMs] = useState(() => serverNow());
  useEffect(() => {
    setNowMs(serverNow());
    const interval = setInterval(() => setNowMs(serverNow()), 1000);
    return () => clearInterval(interval);
  }, []);

  const msLeft = endMs - nowMs;
  const secondsRemaining = Math.max(0, Math.floor(msLeft / 1000));
  const isExpired = msLeft <= 0;                       // fresh every render — never latched
  const settling = isExpired && -msLeft < SETTLING_GRACE_MS;

  // Fire onTimeExpired once per crossing of THIS end time (a later end re-arms it).
  const firedForRef = useRef<number | null>(null);
  useEffect(() => {
    if (isExpired && firedForRef.current !== endMs) {
      firedForRef.current = endMs;
      onTimeExpired?.();
    }
  }, [isExpired, endMs, onTimeExpired]);

  const inSoftClose = isInSoftCloseWindow(end, softCloseWindowSeconds);
  const isExtended = scheduledEndTime && end > scheduledEndTime;

  // Color logic
  const getColorClass = () => {
    if (isExpired) {
      return 'text-gray-600';
    }
    if (secondsRemaining <= 10) {
      return 'text-red-600 font-bold animate-pulse'; // Critical - red flashing
    }
    if (secondsRemaining <= 60) {
      return 'text-red-600 font-bold'; // Final minute - red
    }
    if (inSoftClose) {
      return 'text-yellow-600 font-bold'; // Soft close window - yellow
    }
    return 'text-gray-800'; // Normal
  };

  // Size classes
  const sizeClasses = {
    small: 'text-sm',
    medium: 'text-lg',
    large: 'text-2xl',
  };

  if (settling) {
    return (
      <div className={`text-gray-700 font-semibold ${sizeClasses[size]}`}>
        ⏳ Closing…
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className={`text-gray-600 font-semibold ${sizeClasses[size]}`}>
        ⏰ {closedLotWord(closedStatus, 'upper')}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className={`font-mono ${getColorClass()} ${sizeClasses[size]}`}>
        ⏱️ {formatTimeRemaining(secondsRemaining)}
      </div>
      
      {isExtended && (
        <div className="text-xs text-green-600 font-semibold">
          ⏰ EXTENDED
        </div>
      )}
      
      {inSoftClose && !isExtended && (
        <div className="text-xs text-yellow-600 font-semibold">
          ⚠️ SOFT CLOSE
        </div>
      )}
    </div>
  );
};